export interface ReferencePaginationData {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  hasNext?: boolean;
  hasPrev?: boolean;
}

export interface CurrencyApiRow extends Record<string, unknown> {
  id: string;
  code: string;
  name: string;
  symbol: string | null;
  decimal_places: number;
  is_crypto: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CountryApiRow extends Record<string, unknown> {
  id: string;
  code: string;
  code_alpha3: string | null;
  name: string;
  phone_code: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  currency?: { id: string; code: string; name: string } | null;
}

export interface StateProvinceApiRow extends Record<string, unknown> {
  id: string;
  code: string;
  name: string;
  subdivision_type: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  country?: { id: string; code: string; name: string } | null;
}

export interface ListResponse<T> {
  data: T[];
  pagination: ReferencePaginationData;
}

export interface ListQuery {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
  status?: string;
  search?: string;
  [key: string]: unknown;
}

// API configuration
const API_BASE_URL = '/internal/reference-admin';

import keycloakApi from './keycloak-auth';

const apiCall = async <T>(endpoint: string, options: RequestInit = {}): Promise<T> => {
  // Use absolute endpoint path with shared helper
  return keycloakApi.authenticatedApiCall<T>(`${API_BASE_URL}${endpoint}`, options);
};

const buildQuery = (params: ListQuery): string => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });
  const s = searchParams.toString();
  return s ? `?${s}` : '';
};

export const getCurrencies = (q: ListQuery = {}) =>
  apiCall<ListResponse<CurrencyApiRow>>(`/currencies${buildQuery(q)}`);

export const getCountries = (q: ListQuery = {}) =>
  apiCall<ListResponse<CountryApiRow>>(`/countries${buildQuery(q)}`);

export const getStatesProvinces = (q: ListQuery = {}) =>
  apiCall<ListResponse<StateProvinceApiRow>>(`/states-provinces${buildQuery(q)}`);

// Create functions
export const createCurrency = (data: {
  name: string;
  code: string;
  symbol?: string | null;
  decimal_places?: number;
  is_crypto?: boolean;
  is_active?: boolean;
}) => apiCall<CurrencyApiRow>('/currencies', { method: 'POST', body: JSON.stringify(data) });

export const createCountry = (data: {
  name: string;
  code: string;
  code_alpha3?: string | null;
  phone_code?: string | null;
  currency_code?: string | null;
  is_active?: boolean;
}) => apiCall<CountryApiRow>('/countries', { method: 'POST', body: JSON.stringify(data) });

export const createStateProvince = (data: {
  name: string;
  code: string;
  country_code: string;
  subdivision_type: string;
  is_active?: boolean;
}) =>
  apiCall<StateProvinceApiRow>('/states-provinces', { method: 'POST', body: JSON.stringify(data) });

// Update functions
export const updateCurrency = (
  id: number,
  data: {
    name?: string;
    code?: string;
    symbol?: string | null;
    decimal_places?: number;
    is_crypto?: boolean;
    is_active?: boolean;
  }
) => apiCall<CurrencyApiRow>(`/currencies/${id}`, { method: 'PUT', body: JSON.stringify(data) });

export const updateCountry = (
  id: number,
  data: {
    name?: string;
    code?: string;
    code_alpha3?: string | null;
    phone_code?: string | null;
    currency_code?: string | null;
    is_active?: boolean;
  }
) => apiCall<CountryApiRow>(`/countries/${id}`, { method: 'PUT', body: JSON.stringify(data) });

export const updateStateProvince = (
  id: number,
  data: {
    name?: string;
    code?: string;
    country_code?: string;
    subdivision_type?: string;
    is_active?: boolean;
  }
) =>
  apiCall<StateProvinceApiRow>(`/states-provinces/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

// Delete functions
export const deleteCurrency = (id: number) =>
  apiCall<{ success: boolean; message: string }>(`/currencies/${id}`, { method: 'DELETE' });

export const deleteCountry = (id: number) =>
  apiCall<{ success: boolean; message: string }>(`/countries/${id}`, { method: 'DELETE' });

export const deleteStateProvince = (id: number) =>
  apiCall<{ success: boolean; message: string }>(`/states-provinces/${id}`, { method: 'DELETE' });
